import React, { useState } from 'react';
import classnames from 'classnames';
import styles from "../../styles.module.scss";
import { MdDelete, MdEdit, MdFileDownload, MdShare, MdMoreVert } from 'react-icons/md';
import { Dropdown, Menu } from 'antd';
import { TooltipTop } from '@components/tooltips';
import { HierarchyData } from '../types';
import { EyeIcon } from '@pages/workflow-analytics-page/assets/icons';
import ShareHierarchyModal from './shareHierarchyModal';
import { DataSourceHandler } from '@api/data-source-handler';
import { useSelector } from 'react-redux';
import { RootState } from '@store/types';
import { errorAlert, successAlert } from '@components/toastify/notify-toast';
import { useAppSelector } from '@store/hooks';

type HierachyListProps = {
  data: HierarchyData[] | null;
  handleDelete: (item: HierarchyData) => void;
  handleEdit: (item: HierarchyData) => void;
  handleViewCsvFileMetadata: (item: HierarchyData) => void;
};

const HierachyList: React.FC<HierachyListProps> = ({ data, handleDelete, handleEdit, handleViewCsvFileMetadata }) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedHierarchyId, setSelectedHierarchyId] = useState<number | null>(null);
  const [accessSharedList, setAccessSharedList] = useState<any[]>([]);
  const [featureName, setFeatureName] = useState<string>("");
  const { envVariables: Env } = useSelector((store:RootState)=> store.AccountReducer);
  const currentUser = useAppSelector((state) => state.AccountReducer.activeUserInfo.email);
  const openShareModal = (id: number) => {
    setSelectedHierarchyId(id);
    setModalVisible(true);
  };

  const openUnShareModal = (id: number) => {
    DataSourceHandler.getAccessSharedList(
      `${Env?.REACT_APP_PLATFORM_URL}/asset`, 
      id,
      (response: any) => {
        if(response){
          setSelectedHierarchyId(id);
          setModalVisible(true);
          setAccessSharedList(response)
        }
      },
      (error: any) => {
        errorAlert(error);
      }
    );
  };

  const handleShareSubmit = (selectedUsers: string[]) => {
    const requestData = {
      hierarchyId: Number(selectedHierarchyId),
      userId: selectedUsers,
      action: featureName,
    };
    DataSourceHandler.ShareHierarchy(
      `${Env?.REACT_APP_PLATFORM_URL}/asset`, 
      requestData,
      (response: any) => {
        if(response){
          successAlert(response);
        }
      },
      (error: any) => {
        errorAlert(error);
      }
    );
  
    setModalVisible(false);
  };

   const handleExportHierarchy = (hierarchy: HierarchyData) => {
      DataSourceHandler.ExportHierarchy(`${Env?.REACT_APP_PLATFORM_URL}/asset`, Number(hierarchy.id), (res) => {
        if (res && typeof res === 'string') {
          const blob = new Blob([res], { type: 'text/turtle' });
          
          const url = window.URL.createObjectURL(blob);
          
          const link = document.createElement('a');
          link.href = url;
          link.download = `${hierarchy.name}_export.ttl`;
          
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          window.URL.revokeObjectURL(url);
          
          successAlert(`${hierarchy.name} exported successfully!`);
        } else {
          successAlert(`Failed to export ${hierarchy.name}`);
        }
      });
    }



  const getDropdownMenu = (item: HierarchyData) => (
    <Menu style={{ padding: '8px' }}>
      <Menu.Item key="share" onClick={() => {
        if (currentUser !== item.createdBy) {
          errorAlert("Only admin of this hierarchy have the rights to share/unshare.");
        } else {
          openShareModal(item.id);
          setFeatureName("share");
        }
      }}>
        <MdShare style={{ marginRight: '8px' }} />
        Share
      </Menu.Item>
      <Menu.Item key="unshare" onClick={() => {
        if (currentUser !== item.createdBy) {
          errorAlert("Only admin of this hierarchy have the rights to share/unshare.");
        } else {
          openUnShareModal(item.id);
          setFeatureName("unshare");
        }
      }}>
        <MdShare style={{ marginRight: '8px' }} />
        Unshare
      </Menu.Item>
      {/* <Menu.Item key="export" onClick={() => handleExportHierarchy(item)}>
        <MdFileDownload style={{ marginRight: '8px' }} />
        Export
      </Menu.Item> */}
      <Menu.Item key="delete" onClick={() => {
        if (currentUser !== item.createdBy) {
          errorAlert("Only admin of this hierarchy have the rights to delete");
        } else {
          handleDelete(item);
        }
      }}>
        <MdDelete style={{ marginRight: '8px' }} />
        Delete
      </Menu.Item>
    </Menu>
  );

  return (
    <div className={styles["assetHierarchyList"]}>
      <ul className={classnames(styles['nifi_titles'])}>
        {data?.map((item, index) => (
          <li key={index}>
            <div>
              <div className={styles['key']}>Name</div>
              <TooltipTop title={item.name}>
              <div className={styles['name']}>{item.name}</div>
              </TooltipTop>
            </div>
            <div className={styles['divider']}></div>
            {/* <div>
              <div className={styles['key']}>Edge Name</div>
              <div>{item.edgeName}</div>
            </div> */}
            {/* <div className={styles['divider']}></div> */}


            <div className={styles.actions}>
              <TooltipTop title="Edit">
                <button
                  className={styles.actionButton}
                  onClick={() => handleEdit(item)}
                >
                  <div className={styles.center}>
                    <MdEdit size={"24px"} />
                  </div>
                </button>
              </TooltipTop>
              <TooltipTop title="View">
                <button
                  className={styles.actionButton}
                  onClick={() => handleViewCsvFileMetadata(item)}
                >
                  <div className={styles['center']}>
                    <EyeIcon />
                  </div>
                </button>
              </TooltipTop>
              <Dropdown overlay={getDropdownMenu(item)} trigger={['click']} placement="bottomRight">
                <button className={styles.actionButton}>
                  <div className={styles.center}>
                    <MdMoreVert size={"24px"} />
                  </div>
                </button>
              </Dropdown>
            </div>
          </li>
        ))}
      </ul>

      <ShareHierarchyModal
        featureName={featureName}
        accessSharedList={featureName=='unshare'?accessSharedList:[]}
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        onSubmit={handleShareSubmit}
      />
    </div>
  );
};

export default HierachyList;
